import { db } from './firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp,
  increment
} from 'firebase/firestore';

// Collection name for user counts
const USER_COUNTS_COLLECTION = 'userCounts';

// Constants
const DAILY_FREE_TOMATOES = 10;
const DAILY_FREE_CLAPS = 10;

/**
 * Get or create user counts document
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} User counts data
 */
export const getUserCounts = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const userCountsRef = doc(db, USER_COUNTS_COLLECTION, userId);
    const userCountsSnap = await getDoc(userCountsRef);

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (!userCountsSnap.exists()) {
      // Create new user counts document
      const newUserCounts = {
        tomatoCount: DAILY_FREE_TOMATOES,
        clapCount: DAILY_FREE_CLAPS,
        lastTomatoReset: serverTimestamp(),
        lastClapReset: serverTimestamp(),
        totalTomatoesThrown: 0,
        totalClapsGiven: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(userCountsRef, newUserCounts);
      
      // Return with client-side timestamps for immediate use
      return {
        ...newUserCounts,
        lastTomatoReset: now,
        lastClapReset: now,
        createdAt: now,
        updatedAt: now
      };
    }

    const data = userCountsSnap.data();
    
    // Check if daily reset is needed for tomatoes
    const lastTomatoReset = data.lastTomatoReset?.toDate() || new Date(0);
    const lastTomatoResetDay = new Date(lastTomatoReset.getFullYear(), lastTomatoReset.getMonth(), lastTomatoReset.getDate());
    
    let needsUpdate = false;
    const updates = {};

    if (lastTomatoResetDay < todayStart) {
      // Reset tomatoes to daily limit
      updates.tomatoCount = DAILY_FREE_TOMATOES;
      updates.lastTomatoReset = serverTimestamp();
      needsUpdate = true;
      console.log(`Daily tomato reset for user ${userId}`);
    }

    // Check if daily reset is needed for claps
    const lastClapReset = data.lastClapReset?.toDate() || new Date(0);
    const lastClapResetDay = new Date(lastClapReset.getFullYear(), lastClapReset.getMonth(), lastClapReset.getDate());
    
    if (lastClapResetDay < todayStart) {
      // Reset claps to daily limit
      updates.clapCount = DAILY_FREE_CLAPS;
      updates.lastClapReset = serverTimestamp();
      needsUpdate = true;
      console.log(`Daily clap reset for user ${userId}`);
    }

    // Update if needed
    if (needsUpdate) {
      updates.updatedAt = serverTimestamp();
      await updateDoc(userCountsRef, updates);
      
      // Return updated data
      return {
        ...data,
        ...updates,
        lastTomatoReset: updates.lastTomatoReset ? now : lastTomatoReset,
        lastClapReset: updates.lastClapReset ? now : lastClapReset,
        updatedAt: now
      };
    }

    return data;
  } catch (error) {
    console.error('Error getting user counts:', error);
    throw error;
  }
};

/**
 * Use a tomato (decrease count by 1)
 * @param {string} userId - The user ID
 * @returns {Promise<number>} New tomato count
 */
export const useTomato = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    // First get current counts to check availability
    const counts = await getUserCounts(userId);
    
    if (counts.tomatoCount <= 0) {
      throw new Error('No tomatoes available');
    }

    // Update the count
    const userCountsRef = doc(db, USER_COUNTS_COLLECTION, userId);
    await updateDoc(userCountsRef, {
      tomatoCount: increment(-1),
      totalTomatoesThrown: increment(1),
      updatedAt: serverTimestamp()
    });

    console.log(`Tomato used by ${userId}, new count: ${counts.tomatoCount - 1}`);
    return counts.tomatoCount - 1;
  } catch (error) {
    console.error('Error using tomato:', error);
    throw error;
  }
};

/**
 * Use a clap (decrease count by 1)
 * @param {string} userId - The user ID
 * @returns {Promise<number>} New clap count
 */
export const useClap = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    // First get current counts to check availability
    const counts = await getUserCounts(userId);
    
    if (counts.clapCount <= 0) {
      throw new Error('No claps available');
    }

    // Update the count
    const userCountsRef = doc(db, USER_COUNTS_COLLECTION, userId);
    await updateDoc(userCountsRef, {
      clapCount: increment(-1),
      totalClapsGiven: increment(1),
      updatedAt: serverTimestamp()
    });

    console.log(`Clap used by ${userId}, new count: ${counts.clapCount - 1}`);
    return counts.clapCount - 1;
  } catch (error) {
    console.error('Error using clap:', error);
    throw error;
  }
};

/**
 * Refund a tomato (increase count by 1)
 * @param {string} userId - The user ID
 * @returns {Promise<number>} New tomato count
 */
export const refundTomato = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const userCountsRef = doc(db, USER_COUNTS_COLLECTION, userId);
    await updateDoc(userCountsRef, {
      tomatoCount: increment(1),
      totalTomatoesThrown: increment(-1),
      updatedAt: serverTimestamp()
    });

    // Get updated count
    const updatedDoc = await getDoc(userCountsRef);
    const newCount = updatedDoc.data().tomatoCount;
    
    console.log(`Tomato refunded to ${userId}, new count: ${newCount}`);
    return newCount;
  } catch (error) {
    console.error('Error refunding tomato:', error);
    throw error;
  }
};

/**
 * Refund a clap (increase count by 1)
 * @param {string} userId - The user ID
 * @returns {Promise<number>} New clap count
 */
export const refundClap = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const userCountsRef = doc(db, USER_COUNTS_COLLECTION, userId);
    await updateDoc(userCountsRef, {
      clapCount: increment(1),
      totalClapsGiven: increment(-1),
      updatedAt: serverTimestamp()
    });

    // Get updated count
    const updatedDoc = await getDoc(userCountsRef);
    const newCount = updatedDoc.data().clapCount;
    
    console.log(`Clap refunded to ${userId}, new count: ${newCount}`);
    return newCount;
  } catch (error) {
    console.error('Error refunding clap:', error);
    throw error;
  }
};

/**
 * Add purchased tomatoes
 * @param {string} userId - The user ID
 * @param {number} amount - Number of tomatoes to add
 * @returns {Promise<number>} New tomato count
 */
export const addPurchasedTomatoes = async (userId, amount) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  if (!amount || amount <= 0) {
    throw new Error('Invalid amount');
  }

  try {
    const userCountsRef = doc(db, USER_COUNTS_COLLECTION, userId);
    await updateDoc(userCountsRef, {
      tomatoCount: increment(amount),
      updatedAt: serverTimestamp()
    });

    // Get updated count
    const updatedDoc = await getDoc(userCountsRef);
    const newCount = updatedDoc.data().tomatoCount;
    
    console.log(`${amount} tomatoes added to ${userId}, new count: ${newCount}`);
    return newCount;
  } catch (error) {
    console.error('Error adding purchased tomatoes:', error);
    throw error;
  }
};

/**
 * Add purchased claps
 * @param {string} userId - The user ID
 * @param {number} amount - Number of claps to add
 * @returns {Promise<number>} New clap count
 */
export const addPurchasedClaps = async (userId, amount) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  if (!amount || amount <= 0) {
    throw new Error('Invalid amount');
  }

  try {
    const userCountsRef = doc(db, USER_COUNTS_COLLECTION, userId);
    await updateDoc(userCountsRef, {
      clapCount: increment(amount),
      updatedAt: serverTimestamp()
    });

    // Get updated count
    const updatedDoc = await getDoc(userCountsRef);
    const newCount = updatedDoc.data().clapCount;
    
    console.log(`${amount} claps added to ${userId}, new count: ${newCount}`);
    return newCount;
  } catch (error) {
    console.error('Error adding purchased claps:', error);
    throw error;
  }
};

/**
 * Listen to user counts changes in real-time
 * @param {string} userId - The user ID
 * @param {function} callback - Callback function when data changes
 * @returns {function} Unsubscribe function
 */
export const subscribeToUserCounts = (userId, callback) => {
  if (!userId) {
    console.error('User ID is required for subscription');
    return () => {};
  }

  const userCountsRef = doc(db, USER_COUNTS_COLLECTION, userId);
  
  // For now, we'll use polling since onSnapshot might not be imported
  // In production, you should use onSnapshot for real-time updates
  const checkForUpdates = async () => {
    try {
      const counts = await getUserCounts(userId);
      callback(counts);
    } catch (error) {
      console.error('Error in subscription:', error);
    }
  };

  // Initial check
  checkForUpdates();

  // Poll every 5 seconds (in production, use onSnapshot instead)
  const interval = setInterval(checkForUpdates, 5000);

  // Return unsubscribe function
  return () => clearInterval(interval);
};